// Trim and lower-case so "  A@B.com " and "a@b.com" collide. The CSV parser is
// already configured with `trim: true`, but the helper must be safe on raw
// values too. Mirrors normaliseEmail in helpers/billing-contacts.ts.
export const normaliseCsvValue = (value: string): string =>
  value.trim().toLowerCase();

// Every value that appears more than once, keyed by the normalised value and
// listing the 0-based indices of ALL of its occurrences. Blank cells are
// ignored -- a missing value is a different problem than a duplicated one.
// Keys are ordered by first occurrence.
//
// Note this includes the first occurrence, unlike duplicateEmailIndices in
// helpers/billing-contacts.ts, which deliberately omits it because it drives
// per-row field highlighting ("this row is the redundant one"). Here the
// indices drive a message that has to name every line involved.
export const findDuplicateValues = (
  values: string[],
): Map<string, number[]> => {
  const indicesByValue = new Map<string, number[]>();
  values.forEach((raw, index) => {
    const value = normaliseCsvValue(raw);
    if (!value) return;
    const existing = indicesByValue.get(value);
    if (existing) existing.push(index);
    else indicesByValue.set(value, [index]);
  });

  const duplicates = new Map<string, number[]>();
  indicesByValue.forEach((indices, value) => {
    if (indices.length > 1) duplicates.set(value, indices);
  });
  return duplicates;
};

// A column to check for duplicates: its 0-based index in the row, and the
// human-readable label to name it in error messages. The two always travel
// together, so callers pass one of these rather than parallel arguments.
export type ColumnSpec = {
  index: number;
  label: string;
};

// Human-readable errors for one column of a parsed CSV.
//
// `lineNumbers[i]` is the 1-based physical line number, in the original
// file, of `rows[i]` -- callers derive this from csv-parse's `info.lines`
// so it stays correct even when blank lines were skipped between rows.
// Defaults to treating rows[0] as line 1, for callers with no header and no
// skipped lines.
//
// Rows shorter than column.index + 1 are tolerated: the missing cell reads as
// blank and is skipped rather than throwing or inventing a duplicate.
export const duplicateColumnErrors = (
  rows: string[][],
  column: ColumnSpec,
  lineNumbers: number[] = rows.map((_, index) => index + 1),
): string[] =>
  Array.from(
    findDuplicateValues(rows.map((row) => row[column.index] ?? "")),
  ).map(([, indices]) => {
    const lines = indices.map((index) => lineNumbers[index]).join(", ");
    // Quote the value as the user wrote it in the first offending row rather
    // than the normalised key -- easier to find in the file.
    const original = rows[indices[0]][column.index].trim();
    return `Duplicate ${column.label} "${original}" on lines ${lines}.`;
  });

// The bulk-update-identity CSV is user_id, org_id, current_identity,
// target_identity. Columns 0, 2 and 3 must each be unique within the file:
// the backend applies rows in sequence, so a repeated user ID or target
// identity means one of the updates silently loses and there is no undo.
//
// org_id (column 1) is deliberately NOT checked -- every row of this workflow
// is expected to share the same organisation.
const USER_ID_COLUMN: ColumnSpec = { index: 0, label: "user ID" };
const CURRENT_IDENTITY_COLUMN: ColumnSpec = {
  index: 2,
  label: "current identity",
};
const TARGET_IDENTITY_COLUMN: ColumnSpec = {
  index: 3,
  label: "target identity",
};

// Identifies one occurrence of a pooled value: which row it came from and
// which column it was read from. Needed (unlike findDuplicateValues' bare
// index) because the error message has to name the source column per line.
type ColumnOccurrence = { rowIndex: number; column: ColumnSpec };

// Same idea as findDuplicateValues, but pools cells from several columns of
// the same rows into one namespace instead of one column's cells into a flat
// list -- e.g. current_identity and target_identity must share one
// namespace, since a value can't safely repeat as either. Rows are walked
// outer, columns inner, so a same-row match (current == target) is
// detected and column order is deterministic.
const findDuplicateOccurrencesAcrossColumns = (
  rows: string[][],
  columns: ColumnSpec[],
): Map<string, ColumnOccurrence[]> => {
  const occurrencesByValue = new Map<string, ColumnOccurrence[]>();
  rows.forEach((row, rowIndex) => {
    columns.forEach((column) => {
      const value = normaliseCsvValue(row[column.index] ?? "");
      if (!value) return;
      const occurrence = { rowIndex, column };
      const existing = occurrencesByValue.get(value);
      if (existing) existing.push(occurrence);
      else occurrencesByValue.set(value, [occurrence]);
    });
  });

  const duplicates = new Map<string, ColumnOccurrence[]>();
  occurrencesByValue.forEach((occurrences, value) => {
    if (occurrences.length > 1) duplicates.set(value, occurrences);
  });
  return duplicates;
};

// Errors for a value pool spanning multiple columns of the same rows. Each
// line is annotated with the column it came from, since one duplicate group
// can span more than one column. `groupLabel` names the pool generically
// (e.g. "identity") rather than any single column's label.
export const duplicateAcrossColumnsErrors = (
  rows: string[][],
  columns: ColumnSpec[],
  groupLabel: string,
  lineNumbers: number[] = rows.map((_, index) => index + 1),
): string[] =>
  Array.from(findDuplicateOccurrencesAcrossColumns(rows, columns)).map(
    ([, occurrences]) => {
      const lines = occurrences
        .map(
          ({ rowIndex, column }) =>
            `${lineNumbers[rowIndex]} (${column.label})`,
        )
        .join(", ");
      // Quote the value as written in the first offending cell in file
      // order -- same convention as duplicateColumnErrors.
      const first = occurrences[0];
      const original = rows[first.rowIndex][first.column.index].trim();
      return `Duplicate ${groupLabel} "${original}" on lines ${lines}.`;
    },
  );

// current_identity and target_identity share one namespace: the backend
// applies rows in sequence, so a value repeated as either -- including a row
// mapping a value to itself -- means one update silently loses or the order
// of operations becomes ambiguous (e.g. renaming Alice to Bob, then Bob to
// Carol, in the same file).
export const findIdentityCsvDuplicateErrors = (
  rows: string[][],
  lineNumbers: number[] = rows.map((_, index) => index + 1),
): string[] => [
  ...duplicateColumnErrors(rows, USER_ID_COLUMN, lineNumbers),
  ...duplicateAcrossColumnsErrors(
    rows,
    [CURRENT_IDENTITY_COLUMN, TARGET_IDENTITY_COLUMN],
    "identity",
    lineNumbers,
  ),
];

// The error snackbar renders a single string with no line breaks, so squash
// and cap -- a CSV with hundreds of duplicates would otherwise produce an
// unreadable toast. The in-page Alert lists every error instead.
export const MAX_REPORTED_DUPLICATES = 5;

export const formatDuplicateErrors = (errors: string[]): string => {
  const remaining = errors.length - MAX_REPORTED_DUPLICATES;
  const shown = errors.slice(0, MAX_REPORTED_DUPLICATES).join(" ");
  return remaining > 0
    ? `${shown} ...and ${remaining} more duplicate${
        remaining === 1 ? "" : "s"
      }.`
    : shown;
};
